import { Entity } from '$legacyecstatic';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import { Components } from 'editor/components';
import Canvas from 'editor/components/Canvas';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import CarnivoreSheetLayer from 'editor/components/CarnivoreSheetLayer';
import Space from 'editor/components/Space';
import { renderSpaceComponents } from 'editor/systems/renderSpace';
import Immutable from 'immutable';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Space as SpaceRecord, SpaceId } from 'records/Space';

type Props = PropsWithChildren<{
  sheetComp: CarnivoreSheet;
  index: number;
  width: number;
  height: number;
  documentid: string;
  pageid: string;
}>;

// This component creates and manages the Carnivore "SheetLayer" component.
const ECSSpaceLayer: FC<Props> = (props: Props) => {
  const { sheetComp, index, width, height, documentid, pageid } = props;
  const appStore = useContext(AppStoreContext);
  const world = useContext(ECSWorldContext);
  const { carnCtx } = useContext(ECSEditorContext);
  const [spacesComps, setSpacesComps] = useState<Entity<Components>[] | null>(
    null
  );
  const [sheetLayerComp, setSheetLayerComp] =
    useState<CarnivoreSheetLayer | null>(null);

  //   async
  function createSpaceEntities() {
    world
      .locateAll(renderSpaceComponents)
      .map((entity) => entity.destroyImmediately());

    const canvasComp = new Canvas();
    const sheetSpaces = appStore.state.spaces.get(
      pageid,
      Immutable.OrderedMap<SpaceId, SpaceRecord>()
    );
    if (sheetLayerComp) {
      const _spacesComps: Entity<Components>[] = [];
      sheetSpaces.forEach((s: SpaceRecord) => {
        _spacesComps.push(
          world
            .createEntity()
            .add(sheetComp)
            .add(sheetLayerComp)
            .add(canvasComp)
            .add(new Space(s))
        );
      });
      setSpacesComps(_spacesComps);
    }
  }

  useEffect(() => {
    appStore.actions.getSpaces(documentid);

    // Create Spaces layer canvas
    const _spacesCanvasComp = new Canvas();

    _spacesCanvasComp.canvas.width = width;
    _spacesCanvasComp.canvas.height = height;
    const ctx = _spacesCanvasComp.canvas.getContext('2d');

    // Fill canvas with all white, which will make it "clear" when summing the layers.
    if (ctx) {
      ctx.fillStyle = '#FFFFFF';

      ctx?.fillRect(0, 0, width, height);
    }

    // Create and fill sheet-layer
    const _sheetLayer = carnCtx.ctx.createSheetLayer({
      id: `sheet-layer-${index}`,
      index,
    });

    _sheetLayer.fillBuffer(_spacesCanvasComp.canvas);
    sheetComp.sheet.addSheetLayer(_sheetLayer.id);
    const _sheetLayerComp = new CarnivoreSheetLayer(_sheetLayer);

    setSheetLayerComp(_sheetLayerComp);

    return () => {
      _sheetLayer.destroy();
    };
  }, []);

  useEffect(() => {
    createSpaceEntities();

    return () => {
      spacesComps?.map((s) => {
        s.destroy();
      });
    };
  }, [appStore.state.spaces]);

  const { children } = props;
  return <>{children}</>;
};

export default ECSSpaceLayer;
